import "./MapStyle.scss";
import { IJSLComponent, IJSLVNode, refresh } from "jsl-render";
import { JSLRoute } from "jsl-route";
import { LoadingComponent, EventHandler, numeric } from "jsl-ui";
import { LatLong } from "../../../ajax/types/LatLong";
import { PublicController } from "../../../ajax/PublicController";
import { ExpertController } from "../../../ajax/ExpertController";

export interface IMapOptions {
    height?: string;
    width?: string;
    maxWidth?: string;
    defaultZoom?: number;
    onClick?: EventHandler<{ lat: number, lon: number }>;
    showMarkerOnClick?: boolean;
    showPosOnClick?: boolean;
    defaultToCurrentLocation?: boolean;
}

export interface LongLatC extends LatLong {
    verschleiert?: boolean;
}

export class MapComponent implements IJSLComponent {

    private options: IMapOptions;

    private loading = true;

    private map: any;

    private currentPos: string;

    private wuchsgebietLayer: any = undefined;
    private quadrantenLayer: any = undefined;

    private clickMarker: any;

    constructor(private markers: LongLatC[], options?: IMapOptions) {
        const defaultOptions = {
            defaultToCurrentLocation: true,
            showPosOnClick: true,
            onClick: new EventHandler<{ lat: number, lon: number }>(),
            height: "25rem", maxHeight: "70vh", maxWidth: "55rem", width: "100%", defaultZoom: 7
        };
        this.options = { ...defaultOptions, ...options };
    }

    public render(): IJSLVNode {
        return <div style="position:relative">
            {this.loading ? new LoadingComponent() :
                <div class="map-options">
                    {this.wuchsgebietLayer === undefined ?
                        <button click={this.addWuchsgebiete}>+ Wuchsgebiete</button> :
                        (this.wuchsgebietLayer === null ? new LoadingComponent(1) : <button click={this.removeWuchsgebiete}>- Wuchsgebiete</button>)}
                    {this.quadrantenLayer === undefined ?
                        <button click={this.addQuadranten}>+ Quadranten</button> :
                        (this.quadrantenLayer === null ? new LoadingComponent(1) : <button click={this.removeQuadranten}>- Quadranten</button>)}
                    <div class="m-cur-pos">{this.currentPos}</div>
                    {/* {this.gemeindenLayer === undefined ?
                    <button click={this.addGemeinden}>+ Gemeinden</button> :
                    (this.gemeindenLayer === null ? new LoadingComponent(1) : <button click={this.removeGemeinden}>- Gemeinden</button>)} */}
                </div>
            }

            <div style={"position:relative;width:" + this.options.width +
                ";height:" + this.options.height +
                ";max-width:" + this.options.maxWidth + ";margin:0 auto;"}>
            </div>
            {this.options.defaultToCurrentLocation ?
                <div class="map-legend"><span class="circle"></span>aktuelle Position
            {this.wuchsgebietLayer !== undefined ?
                        <a href="https://bfw.ac.at/rz/bfwcms.web?dok=1144"
                            target="_blank" style="margin-left:1rem">forstlichen Wuchsgebiete - BFW</a> : undefined}
                </div> : null}
        </div>;
    }

    public onCreate(node: IJSLVNode) {
        Promise.all([JSLRoute.lazyLoad("/js/leaflet/leaflet.js"),
        JSLRoute.lazyLoad("/js/leaflet/leaflet.css")]).then(() => {

            const mapOptions: any = {
                maxZoom: 14,
                minZoom: 5,
                maxBounds: [
                    [36, 0],
                    [55, 28]
                ],
                preferCanvas: true
            };
            const L = (window as any).L;
            const map: any = L.map(node.dom, mapOptions).setView([47.5, 14.5], this.options.defaultZoom);

            L.tileLayer("api/tile/get/{z}/{x}/{y}", {
                // attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(map);

            for (const m of this.markers) {
                const marker = L.circleMarker(m, { radius: 3, stroke: false, interactive: false, fillOpacity: 0.5, fillColor: m.verschleiert ? "#4F4F4F" : "#C9121A" });
                // marker.bindPopup("<div><strong>Fund Details</strong></div><div>Lang/Lat, Wer, Wann, ...</div>").openPopup();
                marker.addTo(map);
            }

            if (this.options.showPosOnClick) {
                map.addEventListener("click", (ev) => {
                    const lat = ev.latlng.lat;
                    const lon = ev.latlng.lng;
                    ExpertController.LatLonToMtbq({ lat, lon }).then((mtbq) => {
                        this.currentPos = numeric.format(lat, "0.00000") + " / " + numeric.format(lon, "0.00000") + " Quad: " + mtbq;
                        refresh();
                    });
                });
            }

            if (this.options.showMarkerOnClick) {
                map.addEventListener("click", (ev) => {
                    const lat = ev.latlng.lat;
                    const lon = ev.latlng.lng;
                    if (this.clickMarker != null) {
                        map.removeLayer(this.clickMarker);
                    }
                    this.clickMarker = L.marker({ lon, lat });
                    //L.circleMarker({lon, lat}, {stroke: false, interactive: false, fillOpacity: 0.8, fillColor: "#FF6A00"});
                    this.clickMarker.addTo(map);
                    this.options.onClick.notify({ lat, lon });
                });
            }

            // map.locate({setView: true, maxZoom: 14});
            if (this.options.defaultToCurrentLocation && navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    const latit = position.coords.latitude;
                    const longit = position.coords.longitude;
                    // this is just a marker placed in that position
                    //const markerit = L.marker([position.coords.latitude, position.coords.longitude]).addTo(map);
                    const markerit = L.circleMarker({ lat: latit, lon: longit }, { interactive: false, fillColor: "#23008E" });
                    // move the map to have the location in its center
                    markerit.addTo(map);
                    map.panTo(new L.LatLng(latit, longit));
                });
            }

            this.map = map;
            this.loading = false;
            refresh();
        });
    }

    public onRemove(node: IJSLVNode) {
        if (this.map != null) {
            this.map.off();
            this.map.remove();
        }
    }

    private removeWuchsgebiete() {
        this.map.removeLayer(this.wuchsgebietLayer);
        this.wuchsgebietLayer = undefined;
    }

    private async addWuchsgebiete() {
        this.wuchsgebietLayer = null;
        const L = (window as any).L;
        await JSLRoute.lazyLoad("/js/leaflet/leaflet-shape/shp.js");
        await JSLRoute.lazyLoad("/js/leaflet/leaflet-shape/leaflet.shpfile.js");

        const shp = (window as any).shp;
        shp("/shapes/1027_shape_Wuchs_M31.zip").then((geojson) => {
            const colors = ["#9A0064", "#66009B", "#00CEFF", "#64FF63", "#CF00FF", "#FE6363", "#FEFE00", "#319C00", "#66009B", "#FFCF00", "#FE6363", "#FF9C63", "#009B9C", "#BCBCBD", "#00CEFF", "#66009B", "#63CFFF", "#9C0100", "#00CEFF", "#888485", "#63FF9D", "#9A6400", "#FFCE63", "#FE3000", "#00FFCF", "#FFCF00", "#FFCE63", "#000000"];
            for (let i = 0; i < geojson.features.length; i++) {
                geojson.features[i].properties.__color__ = colors[i];
            }

            //this.printSQLWuchsgebiete(geojson);

            const options = {
                style: (feature) => {
                    return {
                        opacity: 1,
                        fillOpacity: 0.3,
                        color: feature.properties.__color__
                    };
                }, onEachFeature: (feature, layer) => {
                    if (feature.properties) {
                        layer.bindPopup(Object.keys(feature.properties).map((k) => {
                            if (k === "__color__") {
                                return;
                            }
                            return k + ": " + feature.properties[k];
                        }).join("<br />"), {
                            maxHeight: 200
                        });
                    }
                }
            };
            this.wuchsgebietLayer = L.geoJson(geojson, options);
            this.wuchsgebietLayer.addTo(this.map);
            refresh();
        });
    }

    private async addQuadranten() {
        this.quadrantenLayer = null;
        const L = (window as any).L;
        await JSLRoute.lazyLoad("/js/leaflet/leaflet-shape/shp.js");
        await JSLRoute.lazyLoad("/js/leaflet/leaflet-shape/leaflet.shpfile.js");

        const shp = (window as any).shp;
        shp("/shapes/quadranten.zip").then((geojson) => {
            const options = {
                // style: (feature) => {
                //     return {
                //         opacity: 1,
                //         fillOpacity: 0.3,
                //         color: feature.properties.__color__
                //     };
                // }, onEachFeature: (feature, layer) => {
                //     if (feature.properties) {
                //         layer.bindPopup(Object.keys(feature.properties).map((k) => {
                //             if (k === "__color__") {
                //                 return;
                //             }
                //             return k + ": " + feature.properties[k];
                //         }).join("<br />"), {
                //             maxHeight: 200
                //         });
                //     }
                // }
            };
            this.quadrantenLayer = L.geoJson(geojson, options);
            this.quadrantenLayer.addTo(this.map);
            refresh();
        });
    }

    private removeQuadranten() {
        this.map.removeLayer(this.quadrantenLayer);
        this.quadrantenLayer = undefined;
    }

    // private removeGemeinden() {
    //     this.map.removeLayer(this.gemeindenLayer);
    //     this.gemeindenLayer = undefined;
    // }

    // private async addGemeinden() {
    //     this.gemeindenLayer = null;
    //     const L = (window as any).L;
    //     await JSLRoute.lazyLoad("/js/leaflet/leaflet-shape/shp.js");
    //     await JSLRoute.lazyLoad("/js/leaflet/leaflet-shape/leaflet.shpfile.js");

    //     const shp = (window as any).shp;
    //     shp("/shapes/OGDEXT_GEM_1_STATISTIK_AUSTRIA_20200101.zip").then((geojson) => {
    //         //this.printSQLGemeinden(geojson);

    //         // const options = {
    //         //     // style: (feature) => {
    //         //     //     return {
    //         //     //         opacity: 1,
    //         //     //         fillOpacity: 0.3,
    //         //     //         color: feature.properties.__color__
    //         //     //     };
    //         //     // },
    //         //     onEachFeature: (feature, layer) => {
    //         //         if (feature.properties) {
    //         //             layer.bindPopup(Object.keys(feature.properties).map((k) => {
    //         //                 if (k === "__color__") {
    //         //                     return;
    //         //                 }
    //         //                 return k + ": " + feature.properties[k];
    //         //             }).join("<br />"), {
    //         //                 maxHeight: 200
    //         //             });
    //         //         }
    //         //     }
    //         // };
    //         // this.gemeindenLayer = L.geoJson(geojson, options);
    //         // this.gemeindenLayer.addTo(this.map);
    //         //refresh();
    //     });
    // }

    // private printSQLGemeinden(geojson: any) {

    //     let sql = "";
    //     const max = geojson.features.length;
    //     //alert(geojson.features.length); //2117
    //     for (let i = 2000; i < max; i++) {
    //         const polygons = geojson.features[i].geometry.coordinates;
    //         const id = geojson.features[i].properties.id;
    //         const name = geojson.features[i].properties.name;
    //         let polygon = "";
    //         for (let pIdx = 0; pIdx < polygons.length; pIdx++) {
    //             const primaryKey = id + "-" + pIdx;
    //             for (let j = 0; j < polygons[pIdx].length; j++) {
    //                 const points = polygons[pIdx];
    //                 // if (polygons.length > 1 && Array.isArray(points[0][0])) {
    //                 //     points = points[0];
    //                 // }
    //                 if (polygon !== "") {
    //                     polygon += ", ";
    //                 }
    //                 if (points[j] === undefined) {
    //                     continue;
    //                 }
    //                 // if (Array.isArray(points[j][0])) {
    //                 //     debugger;
    //                 // }
    //                 const x = points[j][0].toString();
    //                 const y = points[j][1].toString();
    //                 polygon += x + " " + y;
    //             }
    //             sql += "insert into so.GemeindeShape (Id, Gemeinde, Area) values ('" + primaryKey + "', '" + name + "', 'POLYGON((" + polygon + "))'); ";
    //         }
    //     }
    //     const el = document.createElement("textarea");
    //     el.value = sql;
    //     document.body.appendChild(el);
    // // }

    // private printSQLWuchsgebiete(geojson: any) {

    //     for (let i = 0; i < geojson.features.length; i++) {
    //         const coordinates = geojson.features[i].geometry.coordinates;
    //         const id = geojson.features[i].properties.Wuchsge1;
    //         const name = geojson.features[i].properties.Wuchsnam;
    //         let polygon = "";
    //         for (let j = 0; j < coordinates[0].length; j++) {
    //             if (polygon !== "") {
    //                 polygon += ", ";
    //             }
    //             const x = coordinates[0][j][0].toString();
    //             const y = coordinates[0][j][1].toString();
    //             polygon += x + " " + y;
    //         }
    //         let sql = "insert into so.Naturraum (Id, Name, Area) values (" + id + ", '" + name + "', 'POLYGON((" + polygon + "))')";
    //         console.log(sql);
    //     }
    // }

}
